import { Config } from '@src/core/Config';
import { Bus, type SoundBusManager } from '@src/audio/SoundBusManager';
import { announcerGroup, announcerLine, type AnnouncerLine } from '@src/game/AnnouncerLines';

const VOICE_PATH = '/assets/voice';
const FALLBACK_SECONDS = 1.6;
const CAPTION_PAD_MS = 600;
const MAX_QUEUE = 1;
/** Hold the voice slot while a clip decodes so a second say() cannot start a second voice. */
const LOAD_HOLD_SECONDS = 8;

/** Room, boss, death, and broadcast lines always play. Flavor chatter waits its turn. */
function isSetPiece(groupId: string): boolean {
  if (
    groupId.startsWith('boss_') ||
    groupId.startsWith('floor_mod_') ||
    groupId.startsWith('intro_') ||
    groupId.startsWith('menu_') ||
    groupId.startsWith('commercial_')
  ) {
    return true;
  }
  switch (groupId) {
    case 'opening':
    case 'welcome':
    case 'room_enter':
    case 'treasure_room':
    case 'hazard_room':
    case 'arena_room':
    case 'gauntlet_room':
    case 'sanctum_room':
    case 'room_clear':
    case 'floor_cleared':
    case 'last_portal':
    case 'stall_warning':
    case 'corner_camping':
    case 'dragon_arrive':
    case 'player_death':
    case 'near_death':
    case 'mimic_wake':
    case 'mimic_slain':
    case 'prize_wave':
    case 'cataclysm':
    case 'new_threat_skull':
    case 'new_threat_slime':
    case 'new_threat_bat':
    case 'new_threat_hexer':
    case 'new_threat_cinder':
    case 'new_threat_blight':
    case 'new_threat_wraith':
      return true;
    default:
      return false;
  }
}
const SUB_BASS_RATE = Math.pow(2, Config.audio.subBassCents / 1200);

interface PendingLine {
  line: AnnouncerLine;
  buffer: AudioBuffer | null;
}

function breathSeconds(): number {
  return Config.audio.voiceBreathMs / 1000;
}

function isWeaponCallout(groupId: string): boolean {
  return groupId.startsWith('weapon_');
}

/** A grabbed pickup makes the matching "something's coming" line stale. */
function supersedes(winner: string, loser: string): boolean {
  if (isWeaponCallout(winner) && loser === 'supply_weapon') return true;
  if (winner === 'heal' && loser === 'supply_heart') return true;
  return false;
}

function isStaleChatter(line: AnnouncerLine): boolean {
  return !isSetPiece(line.groupId);
}

function chatterGapSeconds(): number {
  return Config.audio.voiceChatterGapMs / 1000;
}

/**
 * Archmage Vaelen's voice. Plays a clip plus a -1200 cent sub-bass double at 35%
 * gain, ducks the other buses for its duration, and pushes the caption to the HUD.
 * One spoken line at a time: higher priority jumps the queue, stale chatter is
 * dropped, and the next clip waits for the current one plus a short breath.
 */
export class AnnouncerAudioEngine {
  private readonly cache = new Map<string, AudioBuffer | null>();
  private readonly loading = new Map<string, Promise<AudioBuffer | null>>();
  private readonly queue: PendingLine[] = [];
  private current: { line: AnnouncerLine; sources: AudioScheduledSourceNode[]; endsAt: number } | null = null;
  private quietUntil = 0;
  private readonly lastVariant = new Map<string, number>();
  private readonly bags = new Map<string, number[]>();
  private readonly lastPlayedAt = new Map<string, number>();
  private readonly out: GainNode;
  private readonly subGain: GainNode;
  private readonly subFilter: BiquadFilterNode;
  onCaption: ((text: string, durationMs: number) => void) | null = null;

  constructor(private readonly buses: SoundBusManager) {
    const ctx = buses.context;
    this.out = ctx.createGain();
    this.out.gain.value = 1;
    this.out.connect(buses.bus(Bus.Announcer));

    this.subGain = ctx.createGain();
    this.subGain.gain.value = Config.audio.subBassGain;
    this.subFilter = ctx.createBiquadFilter();
    this.subFilter.type = 'lowpass';
    this.subFilter.frequency.value = 420;
    this.subGain.connect(this.subFilter);
    this.subFilter.connect(buses.bus(Bus.Announcer));
  }

  /** Warm the cache so first callouts do not hitch. */
  preload(ids: Iterable<string>): void {
    for (const id of ids) void this.load(id);
  }

  /**
   * Request a line from a group. Draws the next variant from the group's shuffle
   * bag (no repeats until every variant has played) and honors the group's cooldown.
   */
  say(groupId: string): boolean {
    const group = announcerGroup(groupId);
    if (!group || group.variants.length === 0) return false;
    const now = this.buses.now;
    const lastAt = this.lastPlayedAt.get(groupId);
    if (group.cooldown > 0 && lastAt !== undefined && now - lastAt < group.cooldown) return false;
    if (!isSetPiece(groupId) && now < this.quietUntil + chatterGapSeconds()) return false;

    const bag = this.bagFor(groupId, group.variants.length);
    const index = bag[bag.length - 1];
    const line = announcerLine(groupId, index);
    if (!line) return false;
    if (!this.enqueue(line)) return false;
    bag.pop();
    this.lastVariant.set(groupId, index);
    this.lastPlayedAt.set(groupId, now);
    this.pump();
    return true;
  }

  /**
   * Shuffle bag per group: every variant plays once before any repeats. A fresh
   * bag never starts with the variant that closed the previous one.
   */
  private bagFor(groupId: string, n: number): number[] {
    let bag = this.bags.get(groupId);
    if (bag && bag.length > 0) return bag;
    bag = [];
    for (let i = 0; i < n; i++) bag.push(i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = bag[i];
      bag[i] = bag[j];
      bag[j] = tmp;
    }
    const last = this.lastVariant.get(groupId);
    if (n > 1 && last !== undefined && bag[n - 1] === last) {
      const j = Math.floor(Math.random() * (n - 1));
      bag[n - 1] = bag[j];
      bag[j] = last;
    }
    this.bags.set(groupId, bag);
    return bag;
  }

  /** Drops queued (not yet playing) lines, e.g. stale room chatter when leaving a room. */
  clearQueue(): void {
    this.queue.length = 0;
  }

  /** Call once per frame to advance the queue after the current clip and breath. */
  update(): void {
    this.pump();
  }

  /** True while a clip plays or decodes, or during the breath after it. Cutscenes sequence lines on this. */
  isSpeaking(): boolean {
    return this.isBusy() || this.queue.length > 0;
  }

  /** Cut the current line and drop the queue. `immediate` skips the breath so a replacement can start now. */
  interrupt(immediate = false): void {
    this.queue.length = 0;
    this.stopCurrent();
    this.quietUntil = immediate ? this.buses.now : this.buses.now + breathSeconds();
  }

  private speaking(): boolean {
    if (!this.current) return false;
    // Suspended AudioContext clocks do not advance; treat timed playback as finished so
    // waiters (cinematics / menus) cannot spin forever after a gamepad-only unlock miss.
    if (this.buses.context.state !== 'running') return this.current.sources.length === 0;
    return this.buses.now < this.current.endsAt;
  }

  private isBusy(): boolean {
    if (this.speaking()) return true;
    if (this.buses.context.state !== 'running') return false;
    return this.buses.now < this.quietUntil;
  }

  private groupPending(groupId: string): boolean {
    if (this.current?.line.groupId === groupId) return true;
    return this.queue.some((q) => q.line.groupId === groupId);
  }

  private enqueue(line: AnnouncerLine): boolean {
    if (this.groupPending(line.groupId)) return false;

    if (this.current && supersedes(this.current.line.groupId, line.groupId)) return false;
    if (this.queue.some((q) => supersedes(q.line.groupId, line.groupId))) return false;

    if (isStaleChatter(line) && (this.isBusy() || this.queue.length > 0)) return false;

    for (let i = this.queue.length - 1; i >= 0; i--) {
      const queued = this.queue[i].line;
      if (isStaleChatter(queued) || supersedes(line.groupId, queued.groupId)) this.queue.splice(i, 1);
    }

    if (this.current && supersedes(line.groupId, this.current.line.groupId)) {
      this.stopCurrent();
      this.quietUntil = this.buses.now + breathSeconds();
    }

    let i = 0;
    while (i < this.queue.length && this.queue[i].line.priority >= line.priority) i++;
    this.queue.splice(i, 0, { line, buffer: null });
    void this.load(line.id);

    while (this.queue.length > MAX_QUEUE) this.queue.pop();
    return this.queue.some((q) => q.line === line);
  }

  private pump(): void {
    if (this.isBusy()) return;
    this.current = null;
    const next = this.queue.shift();
    if (next) void this.play(next.line);
  }

  private stopCurrent(): void {
    if (!this.current) return;
    for (const s of this.current.sources) {
      try {
        s.stop();
      } catch {
        // already stopped
      }
    }
    this.current = null;
  }

  private async play(line: AnnouncerLine): Promise<void> {
    // Claim the slot synchronously so lines requested while the clip decodes queue behind it.
    const ctx = this.buses.context;
    const pending = { line, sources: [] as AudioScheduledSourceNode[], endsAt: ctx.currentTime + LOAD_HOLD_SECONDS };
    this.current = pending;
    const buffer = await this.load(line.id);
    if (this.current !== pending) return; // interrupted while loading
    const t = ctx.currentTime;
    const sources = pending.sources;
    let duration = FALLBACK_SECONDS;

    if (buffer) {
      duration = buffer.duration;
      const main = ctx.createBufferSource();
      main.buffer = buffer;
      main.connect(this.out);
      main.start(t);
      sources.push(main);

      const sub = ctx.createBufferSource();
      sub.buffer = buffer;
      sub.playbackRate.value = SUB_BASS_RATE;
      sub.connect(this.subGain);
      sub.start(t);
      sub.stop(t + duration);
      sources.push(sub);
    } else {
      this.playStinger(t, sources);
    }

    pending.endsAt = t + duration;
    this.quietUntil = t + duration + breathSeconds();
    this.buses.duck(duration);
    this.onCaption?.(line.caption, duration * 1000 + CAPTION_PAD_MS);
  }

  /** Brass-ish swell used when a voice clip is missing so callouts still land. */
  private playStinger(t: number, sources: AudioScheduledSourceNode[]): void {
    const ctx = this.buses.context;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(0.5, t + 0.08);
    env.gain.exponentialRampToValueAtTime(0.0001, t + FALLBACK_SECONDS);
    env.connect(this.out);
    const freqs = [110, 165, 220];
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(f * 0.9, t);
      osc.frequency.exponentialRampToValueAtTime(f, t + 0.25);
      osc.connect(env);
      osc.start(t);
      osc.stop(t + FALLBACK_SECONDS);
      sources.push(osc);
    }
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 55;
    sub.connect(this.subGain);
    sub.start(t);
    sub.stop(t + FALLBACK_SECONDS);
    sources.push(sub);
  }

  private load(id: string): Promise<AudioBuffer | null> {
    const cached = this.cache.get(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const inflight = this.loading.get(id);
    if (inflight) return inflight;
    const p = (async (): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(`${VOICE_PATH}/${id}.mp3`);
        if (!res.ok) return null;
        const data = await res.arrayBuffer();
        return await this.buses.context.decodeAudioData(data);
      } catch {
        return null;
      }
    })();
    this.loading.set(id, p);
    p.then((buf) => {
      this.cache.set(id, buf);
      this.loading.delete(id);
    });
    return p;
  }
}
